/**
 * Header Component - JavaScript
 * GSAP-powered animations and interactions
 * ============================================
 */

(function() {
  'use strict';

  // Wait for DOM and GSAP
  document.addEventListener('DOMContentLoaded', initHeader);

  function initHeader() {
    const header = document.querySelector('[data-header]');
    const drawer = document.querySelector('[data-drawer]');
    
    if (!header) return;

    // Initialize all header features
    initDrawer(header, drawer);
    initCartAnimation();
  }

  /**
   * Menu Drawer - Two Column Desktop, Accordion Mobile
   */
  function initDrawer(header, drawer) {
    if (!drawer) return;

    const toggleBtn = header.querySelector('[data-drawer-toggle]');
    const closeBtn = drawer.querySelector('[data-drawer-close]');
    const overlay = drawer.querySelector('[data-drawer-overlay]');
    const accordionBtns = drawer.querySelectorAll('[data-drawer-accordion]');
    const submenuTriggers = drawer.querySelectorAll('[data-drawer-submenu-trigger]');
    const submenuPanels = drawer.querySelectorAll('[data-submenu-panel]');
    const submenuContainer = drawer.querySelector('[data-drawer-submenu]');

    let isDesktop = window.innerWidth >= 1024;

    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('drawer-open');
      
      // Focus trap - focus on close button
      setTimeout(() => closeBtn.focus(), 100);

      // GSAP animation for drawer items
      if (typeof gsap !== 'undefined') {
        const items = drawer.querySelectorAll('[data-drawer-item]');
        const footer = drawer.querySelector('[data-drawer-footer]');
        
        gsap.fromTo(items,
          { opacity: 0, x: -30 },
          { 
            opacity: 1, 
            x: 0, 
            duration: 0.4, 
            stagger: 0.05,
            ease: 'power3.out',
            delay: 0.1
          }
        );
        
        if (footer) {
          gsap.fromTo(footer,
            { opacity: 0, y: 20 },
            { 
              opacity: 1, 
              y: 0, 
              duration: 0.3, 
              ease: 'power2.out',
              delay: 0.3
            }
          );
        }
      }

    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('drawer-open');
      
      // Reset submenu state
      hideAllSubmenus();
      
      // Return focus to toggle button
      toggleBtn.focus();
    }

    function showSubmenu(index) {
      // Hide all panels first
      submenuPanels.forEach(panel => {
        panel.classList.remove('is-active');
      });
      
      // Remove selected state from all triggers
      submenuTriggers.forEach(trigger => {
        trigger.classList.remove('is-selected');
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Show the selected panel
      const targetPanel = drawer.querySelector(`[data-submenu-panel="${index}"]`);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
        
        // Find and mark the trigger as selected
        const trigger = drawer.querySelector(`[data-drawer-submenu-trigger][data-menu-index="${index}"]`);
        if (trigger) {
          trigger.classList.add('is-selected');
          trigger.setAttribute('aria-expanded', 'true');
        }

        // GSAP animation for submenu items
        if (typeof gsap !== 'undefined') {
          const links = targetPanel.querySelectorAll('.drawer__submenu-link');
          gsap.fromTo(links,
            { opacity: 0, x: 20 },
            { 
              opacity: 1, 
              x: 0, 
              duration: 0.3, 
              stagger: 0.03,
              ease: 'power2.out'
            }
          );
        }
      }
    }

    function hideAllSubmenus() {
      submenuPanels.forEach(panel => {
        panel.classList.remove('is-active');
      });
      submenuTriggers.forEach(trigger => {
        trigger.classList.remove('is-selected');
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // Event listeners
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isOpen = drawer.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    // Mobile: Accordion for nested nav (2nd level)
    accordionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        
        // Close all other accordions
        accordionBtns.forEach(otherBtn => {
          if (otherBtn !== btn) {
            otherBtn.setAttribute('aria-expanded', 'false');
          }
        });
        
        btn.setAttribute('aria-expanded', !isExpanded);
      });
    });

    // Mobile: Accordion for 3rd level nested nav
    const subnavAccordionBtns = drawer.querySelectorAll('[data-drawer-subnav-accordion]');
    subnavAccordionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        
        // Close other 3rd level accordions within the same parent
        const parentSubnav = btn.closest('.drawer__subnav');
        if (parentSubnav) {
          const siblingBtns = parentSubnav.querySelectorAll('[data-drawer-subnav-accordion]');
          siblingBtns.forEach(otherBtn => {
            if (otherBtn !== btn) {
              otherBtn.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        btn.setAttribute('aria-expanded', !isExpanded);
      });
    });

    // Desktop: Submenu triggers (click only - no hover)
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const menuIndex = trigger.dataset.menuIndex;
        const isCurrentlySelected = trigger.classList.contains('is-selected');
        
        if (isCurrentlySelected) {
          // Toggle off - hide submenu
          hideAllSubmenus();
        } else {
          // Show this submenu
          showSubmenu(menuIndex);
        }
      });
    });

    // Update isDesktop on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        isDesktop = window.innerWidth >= 1024;
        
        // Reset submenu state when switching between mobile/desktop
        if (drawer.getAttribute('aria-hidden') === 'false') {
          hideAllSubmenus();
        }
      }, 100);
    });
  }

  /**
   * Cart Count Animation
   */
  function initCartAnimation() {
    const cartCount = document.querySelector('[data-cart-count]');
    if (!cartCount) return;

    // Observe cart count changes for animation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          // Add bump animation
          cartCount.classList.add('is-bumping');
          
          // Update visibility
          const count = parseInt(cartCount.textContent) || 0;
          if (count > 0) {
            cartCount.classList.add('is-visible');
          } else {
            cartCount.classList.remove('is-visible');
          }
          
          // Remove animation class
          setTimeout(() => {
            cartCount.classList.remove('is-bumping');
          }, 300);
        }
      });
    });

    observer.observe(cartCount, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }


  /**
   * Utility: Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

})();

